import { Directive, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';

@Directive({
  selector: '[enterFocusNext]',
})
export class EnterFocusNextDirective implements OnInit, OnDestroy {
  @Input() wrap = true;
  @Input() includeTextarea = false;
  @Input() includeShiftEnter = false;

  private lastFocusInRoot: HTMLElement | null = null;

  private pendingNext: HTMLElement | null = null;
  private pendingWaitClose = false;

  private onFocusInBind = (e: FocusEvent) => this.onDocFocusIn(e);
  private onKeyDownBind = (e: KeyboardEvent) => this.onDocKeyDown(e);

  constructor(private host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    // ✅ capture：即使元件 stopPropagation，也能收到
    document.addEventListener('focusin', this.onFocusInBind, true);
    document.addEventListener('keydown', this.onKeyDownBind, true);
    console.log('[enterFocusNext] init on', this.host.nativeElement);
  }

  ngOnDestroy(): void {
    document.removeEventListener('focusin', this.onFocusInBind, true);
    document.removeEventListener('keydown', this.onKeyDownBind, true);
  }

  private onDocFocusIn(e: FocusEvent) {
    const root = this.host.nativeElement;
    const target = e.target as HTMLElement | null;
    if (target && root.contains(target)) {
      this.lastFocusInRoot = target;
    }
  }

  private onDocKeyDown(e: KeyboardEvent) {
    if (e.key !== 'Enter') return;
    if ((e as any).isComposing) return;
    if (!this.includeShiftEnter && e.shiftKey) return;

    const root = this.host.nativeElement;
    const active = document.activeElement as HTMLElement | null;

    // ✅ overlay 情境：active 不在 root 時，用 lastFocusInRoot 當 context
    const context =
      (active && root.contains(active) ? active : null) ??
      (this.lastFocusInRoot && root.contains(this.lastFocusInRoot) ? this.lastFocusInRoot : null);

    if (!context) return;

    // textarea 預設保留換行
    if (!this.includeTextarea && context.tagName === 'TEXTAREA') return;

    // ====== 0) 如果目前是 button-like：不要觸發 click/submit，直接跳到下一個「非 button」 ======
    // 目的：Enter 不要觸發 app-vis-button / button / role=button 等互動
    if (this.isButtonLike(context)) {
      e.preventDefault();
      e.stopPropagation();
      this.focusNextFrom(context, root, { skipButtons: true });
      return;
    }

    const target = e.target as HTMLElement | null;

    // ====== 1) mat-select：若 Enter 是發生在 overlay 面板內（選項上）→ 只做「選取」，等關閉後再跳 ======
    // 目的：避免「Enter 選取」同時又落到 Default 跳下一格
    const inMatSelectPanel = !!target?.closest('.mat-select-panel, .mat-option');
    if (inMatSelectPanel) {
      const next = this.computeNextFocusable(context, root, { skipButtons: true });
      if (next) {
        this.pendingNext = next;
        this.pendingWaitClose = true;
        this.waitMatSelectCloseAndJump();
      }
      // 不 preventDefault：讓 Material 正常選取 + 關閉
      return;
    }

    // ====== A) app-vis-datepicker 特判 ======
    const inVisDatepicker = context.closest('app-vis-datepicker');
    if (inVisDatepicker) {
      // 年/月 Enter：交給元件自己做年→月→日
      if (context.classList.contains('year') || context.classList.contains('month')) {
        return;
      }

      // 日 Enter：跳出到下一欄（游標在最後才跳）
      if (context.classList.contains('day')) {
        const input = context as HTMLInputElement;
        const atEnd = (input.selectionEnd ?? 0) === input.value.length;
        if (!atEnd) return;

        e.preventDefault();
        this.focusNextFrom(context, root, { skipButtons: true });
        return;
      }
    }

    // ====== B0) mat-select：Enter 在 trigger 且尚未展開 ======
    // 目標：有資料就打開；若打不開(空選單/尚未載入/disabled)就直接跳下一個
    const combo = this.getComboboxEl(context);
    if (combo && combo.getAttribute('aria-expanded') !== 'true') {
      // 阻止 form submit（但不阻止 Material 嘗試 open）
      e.preventDefault();

      const next = this.computeNextFocusable(context, root, { skipButtons: true });

      // 讓 Material 先跑完 open 的邏輯；下一個 tick 檢查是否真的打開
      setTimeout(() => {
        const opened =
          combo.getAttribute('aria-expanded') === 'true' ||
          !!document.querySelector('.cdk-overlay-container .mat-select-panel');

        // 沒打開 => 視為空/不可開，直接跳下一個
        if (!opened && next) {
          next.focus();
          if (next instanceof HTMLInputElement || next instanceof HTMLTextAreaElement) {
            next.select?.();
          }
        }
      }, 0);

      return;
    }

    // ====== B1) mat-select：已展開狀態（保險）→ 等關閉後跳 ======
    if (this.isMatSelectOpen(context)) {
      const next = this.computeNextFocusable(context, root, { skipButtons: true });
      if (next) {
        this.pendingNext = next;
        this.pendingWaitClose = true;
        this.waitMatSelectCloseAndJump();
      }
      // 不 preventDefault：讓 Material 正常處理 Enter（選取/關閉）
      return;
    }

    // ====== C) Default：一般欄位 Enter 當 Tab（同樣跳過 button） ======
    e.preventDefault();
    this.focusNextFrom(context, root, { skipButtons: true });
  }

  // --------------------------------
  // mat-select helpers
  // --------------------------------
  private getComboboxEl(el: HTMLElement): HTMLElement | null {
    // Material mat-select trigger 常有 role="combobox" + aria-expanded
    return el.closest('[role="combobox"][aria-expanded]') as HTMLElement | null;
  }

  private isMatSelectOpen(el: HTMLElement): boolean {
    const combo = this.getComboboxEl(el);
    return combo?.getAttribute('aria-expanded') === 'true';
  }

  private waitMatSelectCloseAndJump() {
    if (!this.pendingWaitClose || !this.pendingNext) return;

    const root = this.host.nativeElement;
    const start = performance.now();
    const timeoutMs = 1200;

    const tick = () => {
      if (!this.pendingWaitClose || !this.pendingNext) return;

      if (performance.now() - start > timeoutMs) {
        this.pendingWaitClose = false;
        this.pendingNext = null;
        return;
      }

      // ✅ 1) overlay 面板是否仍存在（關閉動畫期間可能還在）
      const overlayStillThere = !!document.querySelector('.cdk-overlay-container .mat-select-panel');

      // ✅ 2) 表單內是否仍有任何 combobox 展開（aria-expanded=true）
      const anyComboboxExpanded = !!root.querySelector('[role="combobox"][aria-expanded="true"]');

      const stillOpen = overlayStillThere || anyComboboxExpanded;

      if (!stillOpen) {
        const next = this.pendingNext;
        this.pendingWaitClose = false;
        this.pendingNext = null;

        setTimeout(() => {
          next.focus();
          if (next instanceof HTMLInputElement || next instanceof HTMLTextAreaElement) {
            next.select?.();
          }
        }, 0);
        return;
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  // --------------------------------
  // button-like detection (Enter 不觸發)
  // --------------------------------
  private isButtonLike(el: HTMLElement): boolean {
    // 原生 button / 在 button 裡
    if (el.tagName === 'BUTTON') return true;
    if (el.closest('button')) return true;

    // input button/submit/reset
    if (el.closest('input[type="button"], input[type="submit"], input[type="reset"]')) return true;

    // aria role button
    if (el.closest('[role="button"]')) return true;

    // 常見：mat-icon 當按鈕用
    if (el.closest('mat-icon[role="button"]')) return true;

    // 備用：可手動標記跳過
    if (el.closest('[data-enter-skip]')) return true;

    return false;
  }

  // -------------------------
  // 通用：跳下一個（支援跳過 button）
  // -------------------------
  private computeNextFocusable(
    current: HTMLElement,
    root: HTMLElement,
    opt?: { skipButtons?: boolean }
  ): HTMLElement | null {
    const focusables = this.getFocusableElements(root);
    const cur = this.findClosestFocusable(current, focusables) ?? current;
    let idx = focusables.indexOf(cur);
    if (idx === -1) return null;

    const maxLoop = focusables.length + 2; // 防呆避免死循環
    for (let step = 0; step < maxLoop; step++) {
      idx = idx + 1;

      let candidate =
        focusables[idx] ??
        (this.wrap
          ? focusables[(idx % focusables.length + focusables.length) % focusables.length]
          : null);

      if (!candidate) return null;

      candidate = this.resolveInnerFocusable(candidate);

      if (opt?.skipButtons && this.isButtonLike(candidate)) continue;

      // 再保險：resolve 後若內部又是按鈕也跳過
      if (opt?.skipButtons) {
        const inner = this.resolveInnerFocusable(candidate);
        if (this.isButtonLike(inner)) continue;
        candidate = inner;
      }

      return candidate;
    }

    return null;
  }

  private focusNextFrom(current: HTMLElement, root: HTMLElement, opt?: { skipButtons?: boolean }) {
    const next = this.computeNextFocusable(current, root, opt);
    if (!next) return;

    next.focus();
    if (next instanceof HTMLInputElement || next instanceof HTMLTextAreaElement) {
      next.select?.();
    }
  }

  // -------------------------
  // focusable 收集（含自訂元件鑽入）
  // -------------------------
  private readonly focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
    '[role="button"]',
  ].join(',');

  private getFocusableElements(root: HTMLElement): HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>(this.focusableSelector))
      .map(el => this.resolveInnerFocusable(el))
      .filter((el, idx, arr) => this.isActuallyFocusable(el) && arr.indexOf(el) === idx);
  }

  private resolveInnerFocusable(el: HTMLElement): HTMLElement {
    if (this.isNativelyFocusable(el)) return el;
    const inner = el.querySelector<HTMLElement>(this.focusableSelector);
    return inner ?? el;
  }

  private isNativelyFocusable(el: HTMLElement): boolean {
    const tag = el.tagName;
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'BUTTON') return true;
    if (tag === 'A' && (el as HTMLAnchorElement).href) return true;
    const tabindex = el.getAttribute('tabindex');
    return tabindex !== null && tabindex !== '-1';
  }

  private isActuallyFocusable(el: HTMLElement): boolean {
    const anyEl = el as any;
    if (anyEl.disabled) return false;
    if (el.getAttribute('aria-disabled') === 'true') return false;

    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (el.getClientRects().length === 0) return false;

    return true;
  }

  private findClosestFocusable(target: HTMLElement, focusables: HTMLElement[]): HTMLElement | null {
    let el: HTMLElement | null = target;
    while (el) {
      const resolved = this.resolveInnerFocusable(el);
      if (focusables.includes(resolved)) return resolved;
      el = el.parentElement;
    }
    return null;
  }
}
